const normalizeClientProfileValue = (value) => {
  if (value === null || value === undefined) return "";
  const raw = typeof value === "string" ? value.trim() : String(value).trim();
  if (!raw) return "";
  if (raw.toUpperCase() === "NAO_INFORMAR") return "";
  return raw;
};

const buildPaths = (prefixes, keys) =>
  prefixes.flatMap((prefix) => keys.map((key) => `${prefix}${key}`));

const pickValue = (data, paths) => {
  if (!data) return "";
  for (const path of paths) {
    const value = path
      .split(".")
      .reduce((acc, key) => (acc && acc[key] != null ? acc[key] : undefined), data);
    const normalized = normalizeClientProfileValue(value);
    if (normalized) return normalized;
  }
  return "";
};

const CLIENT_NICKNAME_PREFIXES = [
  "",
  "user.",
  "profile.",
  "perfil.",
  "cliente.",
  "client.",
];

const CLIENT_PROFILE_PREFIXES = [
  "",
  "user.",
  "profile.",
  "perfil.",
  "cliente.",
  "client.",
  "user.profile.",
  "user.perfil.",
  "cliente.profile.",
  "cliente.perfil.",
  "client.profile.",
  "client.perfil.",
];

const EXPLICIT_CLIENT_NICKNAME_PATHS = buildPaths(CLIENT_NICKNAME_PREFIXES, [
  "apelido",
  "nickname",
]);

const SECONDARY_CLIENT_NICKNAME_PATHS = buildPaths(CLIENT_NICKNAME_PREFIXES, [
  "display_name",
  "name",
]);

const CLIENT_USERNAME_PATHS = buildPaths(CLIENT_PROFILE_PREFIXES, ["username"]);
const CLIENT_EMAIL_PATHS = buildPaths(CLIENT_PROFILE_PREFIXES, ["email"]);

const CLIENT_GENDER_PATHS = buildPaths(CLIENT_PROFILE_PREFIXES, [
  "genero",
  "gender",
  "sexo",
  "genero_label",
  "gender_label",
  "sexo_label",
  "genero_display",
  "gender_display",
  "sexo_display",
  "genero_text",
  "gender_text",
  "sexo_text",
  "genero_nome",
  "gender_name",
  "sexo_nome",
]);

const toComparableToken = (value) =>
  normalizeClientProfileValue(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const collectComparableTokens = (data, paths, mapper = (value) => value) => {
  const values = paths
    .map((path) =>
      path
        .split(".")
        .reduce((acc, key) => (acc && acc[key] != null ? acc[key] : undefined), data)
    )
    .map((value) => mapper(value))
    .map((value) => toComparableToken(value))
    .filter(Boolean);

  return new Set(values);
};

const getEmailLocalPart = (value) => {
  const normalized = normalizeClientProfileValue(value);
  if (!normalized.includes("@")) return normalized;
  return normalized.split("@")[0];
};

const getClientReservedTokens = (data) =>
  new Set([
    ...collectComparableTokens(data, CLIENT_USERNAME_PATHS),
    ...collectComparableTokens(data, CLIENT_EMAIL_PATHS, getEmailLocalPart),
  ]);

const shouldIgnoreGeneratedClientNickname = (data, nickname, reservedTokens) => {
  if (!nickname) return false;
  const completionFlag = resolveClientCompletionFlag(data);
  if (completionFlag === true) return false;
  return reservedTokens.has(toComparableToken(nickname));
};

export function resolveClientNickname(data) {
  const reservedTokens = getClientReservedTokens(data);

  const explicitNickname = pickValue(data, EXPLICIT_CLIENT_NICKNAME_PATHS);
  if (
    explicitNickname &&
    !shouldIgnoreGeneratedClientNickname(data, explicitNickname, reservedTokens)
  ) {
    return explicitNickname;
  }

  for (const path of SECONDARY_CLIENT_NICKNAME_PATHS) {
    const nickname = pickValue(data, [path]);
    if (!nickname) continue;
    if (shouldIgnoreGeneratedClientNickname(data, nickname, reservedTokens)) continue;
    return nickname;
  }

  return "";
}

export function resolveClientGender(data) {
  return pickValue(data, CLIENT_GENDER_PATHS);
}

export function isClientBasicsComplete(data) {
  return Boolean(resolveClientNickname(data)) && Boolean(resolveClientGender(data));
}

export function coerceClientProfileFlag(value) {
  if (value === undefined || value === null) return null;
  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true";
  }
  return Boolean(value);
}

export function resolveClientCompletionFlag(data) {
  if (!data) return null;
  const summary = data.progress_summary || {};
  return coerceClientProfileFlag(
    summary.client_profile_completed ??
      data.client_profile_completed ??
      data.user?.client_profile_completed ??
      summary.profile_completed ??
      data.profile_completed ??
      data.user?.profile_completed
  );
}

export function isClientSetupComplete(data) {
  const completionFlag = resolveClientCompletionFlag(data);
  if (completionFlag !== null) return completionFlag;
  return isClientBasicsComplete(data);
}

export { normalizeClientProfileValue };
